import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { engagementProfileQuery, outputsQuery } from '../api/jedi'
import type { EngagementPhase, OutputLibraryEntry } from '../api/types'
import { phaseNameIn, phasesOf } from '../shell/planReadings'
import { useEngagement } from '../lib/engagement'
import { LoadProblem } from '../shell/LoadProblem'
import { StatusReport } from '../shell/StatusReport'

export const Route = createFileRoute('/_shell/outputs')({
  // Which view is open lives in the URL, so the top bar's Export can land on the
  // status report and a reload keeps it there.
  validateSearch: (search: Record<string, unknown>): { view?: 'report' } =>
    search.view === 'report' ? { view: 'report' } : {},
  component: Outputs,
})

/**
 * Outputs holds two things: the library of what the work produced, and the
 * status report generated from the engagement's records (RFC-0012 §6b). The
 * report is a view of its own because its preview needs the page's width.
 */
function Outputs() {
  const { view } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const engagement = useEngagement()

  return (
    <section className={view === 'report' ? 'surface surface-report' : 'surface surface-wide'}>
      <header className="surface-head">
        <h1>Outputs</h1>
        <nav className="work-lenses" aria-label="Outputs view">
          <button
            type="button"
            className={view === 'report' ? 'work-lens' : 'work-lens work-lens-on'}
            onClick={() => void navigate({ search: {} })}
          >
            Library
          </button>
          <button
            type="button"
            className={view === 'report' ? 'work-lens work-lens-on' : 'work-lens'}
            onClick={() => void navigate({ search: { view: 'report' } })}
          >
            Status report
          </button>
        </nav>
      </header>
      {view === 'report'
        ? <StatusReport engagementId={engagement.id} engagementName={engagement.name} />
        : <Library />}
    </section>
  )
}

/**
 * The Outputs library — `Outputs.dc.html`, and the last inert rail item.
 *
 * The screen states what this surface is in its own body copy: outputs are
 * drafted, reviewed and published inside their work item, and *this is where
 * every output lives afterwards*. So this is a library, not a workspace: nothing
 * is authored here and nothing is published here. Both acts live on the item,
 * which is where the routing law puts them.
 *
 * WHAT THE DRAFT WALL MEANS HERE: the list is what the CALLER may see. Every
 * published output on the engagement, plus the unpublished ones on items they
 * are on. That is enforced in `OutputLibrary` server-side and is deliberately
 * not reproduced in this component - a filter in a view is the thing C-035 rules
 * out by name.
 *
 * FOUR OF THE DESIGN'S TEN FACETS ARE ABSENT, and the surface says so rather
 * than rendering them empty:
 *   - File type (DOCX/PPTX/XLSX) - an output in this build has no file.
 *   - Scope item - no such concept exists anywhere in the model.
 *   - Updated - an output carries no timestamp of its own; the work item's
 *     last-touch is about the item, not about the output.
 *   - Recent, as a view - same reason.
 * A facet with no field behind it is a fixture pretending to be a product.
 */
function Library() {
  const engagement = useEngagement()
  const outputs = useQuery(outputsQuery(engagement.id))
  const profile = useQuery(engagementProfileQuery(engagement.id))
  const phases = phasesOf(profile.data)
  const [state, setState] = useState<string>('all')

  const rows = outputs.data ?? []
  const shown = state === 'all' ? rows : rows.filter((o) => o.state === state)
  const groups = groupByDeliverable(shown)

  return (
    <>
      <p className="surface-sub library-sub">
        What this engagement has produced. Outputs are drafted, reviewed and published inside
        their work item; this is where they live afterwards.
      </p>

      {/* The ratified lens-tab pattern, reused rather than reinvented. */}
      <nav className="work-lenses" aria-label="Filter by state">
        {(['all', 'draft', 'in-review', 'published'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={state === s ? 'work-lens work-lens-on' : 'work-lens'}
            onClick={() => setState(s)}
          >
            {s === 'all' ? 'All' : s === 'in-review' ? 'In review' : s === 'draft' ? 'Draft' : 'Published'}
          </button>
        ))}
      </nav>

      {outputs.isPending ? (
        <p className="surface-note">Reading.</p>
      ) : !outputs.data ? (
        // Finished and holding nothing means the read FAILED, which is not an
        // empty library. Until 2026-09-17 this fell through to the sentence
        // below and told people nothing had been produced.
        <LoadProblem error={outputs.error} />
      ) : rows.length === 0 ? (
        // Absence stated, never implied by a quiet surface.
        <p className="surface-note">
          Nothing has been produced on this engagement yet. Outputs are named inside a work item,
          and appear here as soon as they exist.
        </p>
      ) : shown.length === 0 ? (
        <p className="surface-note">
          {rows.length === 1
            ? 'The one output here is not in that state.'
            : `None of the ${rows.length} outputs here are in that state.`}
        </p>
      ) : (
        groups.map(([deliverable, entries]) => (
          <div key={deliverable} className="output-group">
            <h2 className="surface-h2">{deliverable}</h2>
            <ul className="output-library">
              {entries.map((o) => (
                <li key={o.id} className="output-library-row">
                  <span className="output-library-name">{o.name}</span>
                  <span className="output-library-state">{stateLine(o)}</span>
                  <span className="output-library-meta">{metaLine(o, phases)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}

      <p className="surface-note surface-note-gap">
        This library does not yet carry a file type, a scope item, or when an output was last
        touched. Nothing in the build records them, so nothing here claims them.
      </p>
    </>
  )
}

/**
 * Grouped by what it fulfils, which is the link that matters: an artifact
 * fulfils a deliverable, and that is the contractual thread the customer sees.
 * Unpublished work fulfils nothing yet and says so.
 */
function groupByDeliverable(rows: OutputLibraryEntry[]): [string, OutputLibraryEntry[]][] {
  const NOT_YET = 'Not yet fulfilling a deliverable'
  const map = new Map<string, OutputLibraryEntry[]>()
  for (const row of rows) {
    const key = row.deliverableName ?? NOT_YET
    map.set(key, [...(map.get(key) ?? []), row])
  }
  // The unfulfilled group sorts last: it is work in progress, not the record.
  return [...map.entries()].sort(([a], [b]) =>
    a === NOT_YET ? 1 : b === NOT_YET ? -1 : a.localeCompare(b))
}

function stateLine(o: OutputLibraryEntry): string {
  const state = o.state === 'in-review'
    ? o.reviewerName ? `In review with ${o.reviewerName}` : 'In review'
    : o.state === 'published' ? 'Published' : 'Draft'
  return `v${o.version} · ${state}`
}

/** Everything true about this output, and nothing that is not. */
function metaLine(o: OutputLibraryEntry, phases: readonly EngagementPhase[]): string {
  const parts: string[] = []
  if (o.format) parts.push(o.format)
  // The deliverable's phase, by its name on this engagement (ADR-0061).
  if (o.phase) parts.push(phaseNameIn(phases, o.phase))
  if (o.audience) parts.push(`visible to ${o.audience.toLowerCase()}`)
  if (o.withheldCount) parts.push(`${o.withheldCount} internal items excluded`)
  // `made in W-118 · Baseline the requirements`. The design renders the
  // reference and the title as a label; the build had only the title, which on
  // real data is a SENTENCE in the middle of a meta line. The reference is null
  // on an item born before the numbering, and the line then reads as it always
  // did rather than carrying a gap.
  parts.push(o.workItemReference
    ? `made in ${o.workItemReference} · ${o.workItemTitle}`
    : `made in ${o.workItemTitle}`)
  return parts.join(' · ')
}
